import { Helmet } from "react-helmet-async";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  image?: string;
  faqItems?: FAQItem[];
  breadcrumbs?: BreadcrumbItem[];
  wordCount?: number;
}

const SEOHead = ({ 
  title, 
  description, 
  path = "",
  type = "website",
  publishedTime,
  modifiedTime,
  keywords = [],
  image = "https://mysabbat.dk/og-image.png",
  faqItems = [],
  breadcrumbs = [],
  wordCount
}: SEOHeadProps) => {
  const baseUrl = "https://mysabbat.dk";
  const fullUrl = `${baseUrl}${path}`;
  const imageUrl = image.startsWith("http") ? image : `${baseUrl}${image}`;

  // Default keywords for MySabbat
  const defaultKeywords = [
    "sabbatår",
    "gap year",
    "rejse",
    "sabbatår guide",
    "sabbatår planlægning",
    "højskole",
    "backpacking",
    "working holiday"
  ];

  const allKeywords = [...new Set([...keywords, ...defaultKeywords])];

  // JSON-LD structured data for Article (enhanced)
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "url": fullUrl,
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    ...(wordCount && { "wordCount": wordCount }),
    "author": {
      "@type": "Organization",
      "name": "MySabbat",
      "url": baseUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/favicon.png`
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "MySabbat",
      "url": baseUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/favicon.png`,
        "width": 512,
        "height": 512
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  };

  // JSON-LD structured data for Website
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "MySabbat",
    "url": baseUrl,
    "description": "Din guide til det perfekte sabbatår",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/søg?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  // JSON-LD structured data for FAQ (for Google "People Also Ask" rich snippets)
  const faqSchema = faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // JSON-LD structured data for Breadcrumbs
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  } : null;

  // Choose base structured data based on type
  const baseStructuredData = type === "article" ? articleSchema : websiteSchema;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords.join(", ")} />
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content="index, follow" />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="MySabbat" />
      <meta property="og:locale" content="da_DK" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Article-specific meta */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Base structured data (Article or Website) */}
      <script type="application/ld+json">
        {JSON.stringify(baseStructuredData)}
      </script>

      {/* FAQ structured data (separate script for better parsing) */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {/* Breadcrumb structured data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
